import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  InputAdornment,
  Typography,
  Box,
  CircularProgress,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Search, LocationOn, Person, ShoppingBag } from "@mui/icons-material";
import { Button } from "./Button";
import { orderService, Order } from "@/services/order.service";
import { zoneService } from "@/services/zone.service";
import { formatCustomerName } from "@/utils/formatCustomerName";

export interface DriverOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface SelectOrderDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (order: Order, driverId?: string) => void;
  deliveryDate: string;
  excludeOrderIds?: string[];
  driverId?: string;
  drivers?: DriverOption[];
}

/**
 * Dialog pour sélectionner une commande non assignée et l'assigner à un livreur
 */
export function SelectOrderDialog({
  open,
  onClose,
  onSelect,
  deliveryDate,
  excludeOrderIds = [],
  driverId: driverIdProp,
  drivers = [],
}: SelectOrderDialogProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState("");
  const [zoneFilter, setZoneFilter] = useState<string>("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedDriverId, setSelectedDriverId] = useState<string>("");

  const driverId = driverIdProp ?? (selectedDriverId || undefined);
  const showDriverSelect = !driverIdProp && drivers.length > 0;

  const { data: orders = [], isLoading } = useQuery({
    queryKey: [
      "orders",
      "unassigned",
      deliveryDate,
      [...excludeOrderIds].sort().join(","),
    ],
    queryFn: async () => {
      const result = await orderService.getAll({
        deliveryDateFrom: deliveryDate,
        deliveryDateTo: deliveryDate,
      });
      const list = Array.isArray(result) ? result : result.orders;
      return list.filter(
        (o) => !excludeOrderIds.includes(o.id) && o.status !== "CANCELLED",
      );
    },
    enabled: open,
  });

  // Récupérer les zones pour le filtre
  const { data: zones = [] } = useQuery({
    queryKey: ["zones"],
    queryFn: () => zoneService.getAll(),
  });

  // Filtrer les commandes
  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const q = searchQuery.toLowerCase();
      const customerLabel = order.customer ? formatCustomerName({
        firstName: order.customer.firstName,
        lastName: order.customer.lastName,
        type: order.customer.type as "INDIVIDUAL" | "INSTITUTION" | "COMPANY",
      }) : "";
      const matchesSearch =
        !searchQuery ||
        order.orderNumber.toLowerCase().includes(q) ||
        customerLabel.toLowerCase().includes(q);
      const matchesZone =
        zoneFilter === "all" || order.customerAddress?.zone?.id === zoneFilter;
      return matchesSearch && matchesZone;
    });
  }, [orders, searchQuery, zoneFilter]);

  const handleSelect = () => {
    if (!selectedOrder) return;
    if (showDriverSelect && !driverId) return;
    onSelect(selectedOrder, driverId);
    setSelectedOrder(null);
    setSelectedDriverId("");
    setSearchQuery("");
    setZoneFilter("all");
  };

  const handleClose = () => {
    setSelectedOrder(null);
    setSelectedDriverId("");
    setSearchQuery("");
    setZoneFilter("all");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingBag />
          <Typography variant="h6">Sélectionner une commande</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {showDriverSelect && (
          <FormControl size="small" sx={{ minWidth: 200, mb: 2 }} fullWidth>
            <InputLabel>Livreur</InputLabel>
            <Select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              label="Livreur"
            >
              <MenuItem value="">
                <em>Sélectionner un livreur</em>
              </MenuItem>
              {drivers.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.firstName} {d.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            placeholder="Rechercher une commande..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Zone</InputLabel>
            <Select
              value={zoneFilter}
              onChange={(e) => setZoneFilter(e.target.value)}
              label="Zone"
            >
              <MenuItem value="all">Toutes</MenuItem>
              {zones.map((zone) => (
                <MenuItem key={zone.id} value={zone.id}>
                  {zone.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Liste des commandes */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || zoneFilter !== "all"
                ? "Aucune commande ne correspond aux critères."
                : "Aucune commande non assignée pour cette date."}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflowY: "auto" }}>
            {filteredOrders.map((order) => (
              <ListItem
                key={order.id}
                disablePadding
                sx={{
                  border: 1,
                  borderColor:
                    selectedOrder?.id === order.id ? "primary.main" : "divider",
                  borderRadius: 1,
                  mb: 1,
                  bgcolor:
                    selectedOrder?.id === order.id
                      ? "primary.light"
                      : "background.paper",
                }}
              >
                <ListItemButton onClick={() => setSelectedOrder(order)}>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, fontFamily: "monospace" }}
                        >
                          #{order.orderNumber}
                        </Typography>
                        {order.customerAddress?.zone && (
                          <Chip
                            label={`Zone ${order.customerAddress.zone.name}`}
                            size="small"
                            sx={{ bgcolor: "info.light", color: "info.dark" }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          mt: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Person
                            sx={{ fontSize: 14, color: "text.secondary" }}
                          />
                          <Typography variant="body2">
                            {order.customer
                              ? formatCustomerName({
                                  firstName: order.customer.firstName,
                                  lastName: order.customer.lastName,
                                  type: order.customer.type as "INDIVIDUAL" | "INSTITUTION" | "COMPANY",
                                }) || "Client inconnu"
                              : "Client inconnu"}
                          </Typography>
                        </Box>
                        {(order.customerAddress?.address ?? order.customer?.address) && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <LocationOn
                              sx={{ fontSize: 14, color: "text.secondary" }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {order.customerAddress?.address ?? order.customer?.address}
                              {(order.customerAddress?.city ?? order.customer?.city) &&
                                `, ${order.customerAddress?.city ?? order.customer?.city}`}
                            </Typography>
                          </Box>
                        )}
                        {order.deliveryTimeSlot && (
                          <Typography variant="caption" color="text.secondary">
                            Créneau: {order.deliveryTimeSlot}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {order.totalAmount.toLocaleString("fr-FR", {
                        style: "currency",
                        currency: "EUR",
                      })}
                    </Typography>
                  </ListItemSecondaryAction>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        <Button
          variant="primary"
          onClick={handleSelect}
          disabled={
            !selectedOrder || (showDriverSelect && !driverId)
          }
        >
          Sélectionner
        </Button>
      </DialogActions>
    </Dialog>
  );
}
